/**
 * Recent-conversations + recent-searches helpers for the morph palette's
 * empty body. Lifted from the previous `shell/starter-list.tsx` so the
 * data layer is reusable across surfaces.
 */

import useSWR from 'swr'

import { fetcher } from './api-client'

const LS_RECENT_SEARCHES = 'agent-embed:recent-searches'

export interface RecentConversation {
  id: string
  title: string | null
  updatedAt: string
}

function readLs(): string[] {
  if (typeof window === 'undefined') {
    return []
  }
  try {
    const raw = window.localStorage.getItem(LS_RECENT_SEARCHES)
    if (!raw) {
      return []
    }
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.slice(0, 5) : []
  } catch {
    return []
  }
}

/** Push a query onto the recent-searches list. Call this after submit. */
export function pushRecentSearch(text: string): void {
  if (typeof window === 'undefined' || !text.trim()) {
    return
  }
  try {
    const current = readLs()
    const next = [text, ...current.filter((q) => q !== text)].slice(0, 5)
    window.localStorage.setItem(LS_RECENT_SEARCHES, JSON.stringify(next))
  } catch {
    /* private mode / quota — silent */
  }
}

export function readRecentSearches(): string[] {
  return readLs()
}

export function relativeTime(iso: string): string {
  const then = new Date(iso).getTime()
  const now = Date.now()
  const diff = now - then
  if (diff < 60_000) {
    return 'just now'
  }
  if (diff < 3_600_000) {
    return `${Math.floor(diff / 60_000)} min ago`
  }
  if (diff < 86_400_000) {
    return `${Math.floor(diff / 3_600_000)} hr ago`
  }
  if (diff < 86_400_000 * 7) {
    return `${Math.floor(diff / 86_400_000)} d ago`
  }
  return new Date(iso).toLocaleDateString()
}

/** Background reads here should never escalate to host-visible toasts.
 *  The user opened RFP / HQ / etc. — they didn't ask for "show me
 *  recent agent conversations." If the agent backend is unreachable
 *  (cross-origin auth missing in dev, network blip, 4xx, 5xx, anything),
 *  silently degrade to an empty RECENT section. The bare `fetcher`
 *  throws ApiError on non-OK; that propagates to the host's SWR
 *  onError handler, which (in RFP's case) only suppresses FetchError
 *  not ApiError → the user sees a "Authentication required" toast for
 *  a hook they never knowingly invoked. Tolerant wrapper fixes that. */
async function tolerantRecentFetcher(url: string): Promise<RecentConversation[]> {
  try {
    return await fetcher<RecentConversation[]>(url)
  } catch {
    return []
  }
}

/**
 * [PROD-1265] The recency floor these preview surfaces rely on. It used to be
 * hardcoded in the server's `list()`, which made every chat idle for a month
 * unreachable from the agent app's /chats too. /chats now pages through the
 * whole history and passes no floor; these surfaces show a fixed preview with
 * no way to ask for more, so they ask for the 30-day window explicitly and
 * behave exactly as they did before.
 */
export const RECENT_MAX_AGE_DAYS = 30

export const CHAT_LIST_PATH = '/api/recall/conversations'

/** The list URL every embed PREVIEW surface fetches, floor included. */
export function conversationsUrl(hostApp?: string): string {
  const params = new URLSearchParams({ maxAgeDays: String(RECENT_MAX_AGE_DAYS) })
  if (hostApp) {
    params.set('hostApp', hostApp)
  }
  return `${CHAT_LIST_PATH}?${params.toString()}`
}

/**
 * [PROD-1265] The history panel BROWSES rather than previewing, so it takes no
 * `maxAgeDays` floor and pages instead. The contrast with `conversationsUrl`
 * above IS the rule: a surface that can ask for more must never be floored, and
 * one that cannot must be — otherwise it either hides history behind a cap
 * nobody can lift, or advertises a four-month-old chat as recent.
 *
 * Page 1 deliberately carries no query string at all, so it shares the server's
 * default and the browser cache entry with every other unfloored page-1 read.
 */
export function chatHistoryUrl(cursor?: { updatedBefore: string; limit: number }): string {
  if (!cursor) {
    return CHAT_LIST_PATH
  }
  return `${CHAT_LIST_PATH}?updatedBefore=${encodeURIComponent(cursor.updatedBefore)}&limit=${cursor.limit}`
}

/**
 * Append a fetched page, keeping the FIRST copy of any id. The server's cursor
 * is `lte`, not `lt` — `updatedAt` is a TIMESTAMP(3) with no unique index, so
 * `lt` would silently swallow rows tied on the boundary — which means every
 * page after the first re-serves the row it was cut from.
 */
export function mergeById<T extends { id: string }>(...pages: T[][]): T[] {
  const seen = new Set<string>()
  const out: T[] = []
  for (const page of pages) {
    for (const row of page) {
      if (!seen.has(row.id)) {
        seen.add(row.id)
        out.push(row)
      }
    }
  }
  return out
}

/** SWR-backed reader for the user's recent conversations. Returns the
 *  top `limit` rows; defaults to 3 to match the morph palette's RECENT
 *  section. When `hostApp` is set, the server filters to conversations
 *  that originated in that host — RECENT in Tasker shows only Tasker
 *  chats, not RFP-Gremlin or HQ ones. */
export function useRecentConversations(
  limit = 3,
  hostApp?: string,
): {
  conversations: RecentConversation[]
  isLoading: boolean
} {
  const url = conversationsUrl(hostApp)
  const { data, isLoading } = useSWR<RecentConversation[]>(url, tolerantRecentFetcher, {
    revalidateOnFocus: false,
    dedupingInterval: 60_000,
    shouldRetryOnError: false,
  })
  return {
    conversations: (data ?? []).slice(0, limit),
    isLoading,
  }
}
